import React from "react";
import "./AIChat.scss";
import {
  GenericAIResponseCard,
  UpdateOrderInfoAICard,
  UpdateMultipleFieldsAICard,
  UpdateFieldsFormAICard,
  UpdateSingleFieldAICard,
  UpdateFieldSuccessAICard,
  UpdateFieldFailedAICard,
  UpdateFieldsFormSuccessAICard,
  UpdateFieldsFormFailedAICard,
  UpdateEffortsSavingsAICard,
  AILoadingCard,
  ActivityLogCard,
  AttachFilesAICard,
} from "./AICards";

// Renders appropriate AI response card based on message type and state
export const AIChat = ({
  message,
  orderDetails,
  onActionClick,
  onFormValuesChange,
  onRefreshOrderDetails,
  chatSource,
  disableButtons = false,
  disableRecommendedActionButtons = false, // disable recommended action changes 1st
  hideRecommendedActions = false,
}) => {
  // Determine which card to render based on message data
  const effectiveSource = message.source || chatSource || "openOrders";
  const hasExplicitUiFields =
    Array.isArray(message.ui_fields) && message.ui_fields.length > 0;
  const hasFlagDrivenUiFields =
    (message.efforts_flag || message.savings_flag) &&
    Array.isArray(orderDetails) &&
    orderDetails.length > 0;
  const shouldShowFallbackActionTaken =
    effectiveSource === "actionNeeded" &&
    !message.activityLog &&
    !message.suppressRecommendedActions &&
    !message.loading &&
    (message.updateOrderInfo ||
      message.updateSingleField ||
      message.ui_fields_flag ||
      message.updateFieldFailed ||
      message.updateFieldsForm ||
      message.updateFieldsFormFailed);

  const renderActionTakenFooter = () => {
    if (!shouldShowFallbackActionTaken) {
      return null;
    }

    return (
      <div className="order-chat-ai-action-footer">
        <div className="order-ai-actions">
          <button
            className={`order-ai-action-btn ${disableButtons ? "disabled" : ""}`.trim()}
            onClick={() => !disableButtons && onActionClick?.("Action Taken")}
            disabled={disableButtons}
          >
            Action Taken
          </button>
        </div>
      </div>
    );
  };

  // Render AILoadingCard when loading flag exists
  if (message.loading) {
    return (
      <AILoadingCard message={message} loadingText={message.loadingText} />
    );
  }

  // Render AttachFilesAICard when attachFilesCard flag exists
  if (message.attachFilesCard) {
    return <AttachFilesAICard message={message} />;
  }

  // Render UpdateOrderInfoAICard when updateOrderInfo flag exists
  if (message.updateOrderInfo) {
    return (
      <UpdateOrderInfoAICard
        message={message}
        onActionClick={onActionClick}
        orderDetails={orderDetails}
      />
    );
  }

  // Render UpdateEffortsSavingsAICard when effortsSavingsForm flag exists
  if (message.effortsSavingsForm) {
    return (
      <UpdateEffortsSavingsAICard
        message={message}
        onActionClick={onActionClick}
        orderDetails={orderDetails}
        onRefreshOrderDetails={onRefreshOrderDetails}
      />
    );
  }

  // Render UpdateMultipleFieldsAICard when updateMultipleFields flag exists
  if (message.updateMultipleFields) {
    return (
      <UpdateMultipleFieldsAICard
        message={message}
        onActionClick={onActionClick}
        orderDetails={orderDetails}
      />
    );
  }

  // Render UpdateSingleFieldAICard when updateSingleField flag exists
  if (message.updateSingleField) {
    return (
      <UpdateSingleFieldAICard
        message={message}
        onActionClick={onActionClick}
        orderDetails={orderDetails}
        onRefreshOrderDetails={onRefreshOrderDetails}
      />
    );
  }

  // Render UpdateSingleFieldAICard when ui_fields_flag is true (dynamic fields from AI response)
  if (
    (message.ui_fields_flag && hasExplicitUiFields) ||
    hasFlagDrivenUiFields
  ) {
    return (
      <UpdateSingleFieldAICard
        message={message}
        onActionClick={onActionClick}
        orderDetails={orderDetails}
        onRefreshOrderDetails={onRefreshOrderDetails}
      />
    );
  }

  // Render UpdateFieldSuccessAICard when updateFieldSuccess flag exists
  if (message.updateFieldSuccess) {
    return (
      <UpdateFieldSuccessAICard
        message={message}
        onActionClick={onActionClick}
      />
    );
  }

  // Render UpdateFieldFailedAICard when updateFieldFailed flag exists
  if (message.updateFieldFailed) {
    return (
      <>
        <UpdateFieldFailedAICard
          message={message}
          onActionClick={onActionClick}
          // disableButtons={disableButtons} // disable recommended action changes 1st
          disableButtons={disableButtons || disableRecommendedActionButtons} // disable recommended action changes 1st
        />
        {renderActionTakenFooter()}
      </>
    );
  }

  // Render UpdateFieldsFormAICard when updateFieldsForm flag exists
  if (message.updateFieldsForm) {
    return (
      <>
        <UpdateFieldsFormAICard
          message={message}
          onActionClick={onActionClick}
          onFormValuesChange={onFormValuesChange}
        />
        {renderActionTakenFooter()}
      </>
    );
  }

  // Render UpdateFieldsFormSuccessAICard when updateFieldsFormSuccess flag exists
  if (message.updateFieldsFormSuccess) {
    return (
      <UpdateFieldsFormSuccessAICard
        message={message}
        onActionClick={onActionClick}
      />
    );
  }

  // Render UpdateFieldsFormFailedAICard when updateFieldsFormFailed flag exists
  if (message.updateFieldsFormFailed) {
    return (
      <>
        <UpdateFieldsFormFailedAICard
          message={message}
          onActionClick={onActionClick}
        />
        {renderActionTakenFooter()}
      </>
    );
  }

  // Render ActivityLogCard when activityLog flag exists
  if (message.activityLog) {
    return (
      <>
        <ActivityLogCard message={message} />
        {renderActionTakenFooter()}
      </>
    );
  }

  // Render GenericAIResponseCard for all other cases
  // This handles user-sent messages and AI responses that don't match specific card types
  return (
    <GenericAIResponseCard
      message={message}
      orderDetails={orderDetails}
      source={effectiveSource}
      onActionClick={onActionClick}
      // disableButtons={disableButtons} // disable recommended action changes 1st
      disableButtons={disableButtons || disableRecommendedActionButtons} // disable recommended action changes 1st
      hideRecommendedActions={hideRecommendedActions}
    />
  );
};
